import { yupResolver } from "@hookform/resolvers/yup";
import { InvoiceConfiguration, paymentsApi } from "api";
import { DeleteButton } from "components/atoms/Buttons";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { useToaster } from "utils";
import * as yup from "yup";
import { PaziaButton, PaziaDatepicker, PaziaFormButton, PaziaInput, PaziaTextarea } from "components/atoms";
import { useEffect, useRef, useState } from "react";

// Helper function to set time to 11:00:00 to avoid timezone issues
const setDateToEleven = (date: Date | null | undefined): Date => {
  if (!date) return new Date();
  const newDate = new Date(date);
  newDate.setHours(11, 0, 0, 0);
  return newDate;
};

interface GenerateCustomInvoiceFormProps {
  configuration: InvoiceConfiguration;
  onSuccess: () => void;
  onCancel: () => void;
  paymentId: number;
  courseId?: number;
}

const FORM_NAMES = {
  // Header fields (readonly)
  TOP_LEFT_LOGO_PATH: "topLeftLogoPath",
  TOP_RIGHT_TABLE_HEADER: "topRightTableHeader",
  CREATION_DATE: "creationDate",
  SALE_DATE: "saleDate",
  PAYMENT_METHOD: "paymentMethod",
  ORDER_NUMBER: "orderNumber",

  // Seller fields (readonly)
  SELLER_NAME: "sellerName",
  SELLER_ADDRESS: "sellerAddress",
  SELLER_NIP: "sellerNip",
  SELLER_BANK_ACCOUNT: "sellerBankAccount",

  // Editable fields
  ROWS: "rows",
  BUYER: "buyer",
  BUYER_ADDRESS: "buyerAddress",
  BUYER_NIP: "buyerNip",
  RECIPIENT: "recipient",
  RECIPIENT_ADDRESS: "recipientAddress",
  RECIPIENT_NIP: "recipientNip",
  PREVIOUS_ADVANCED_PAYMENTS: "previousAdvancedPayments",
  RATES_SETTLEMENT: "ratesSettlement",
  ORDER_SUMMARY_ROWS: "orderSummaryRows",

  // Invoice sums (editable)
  INVOICE_SUMS_PAID: "invoiceSumsPaid",
  INVOICE_SUMS_TO_PAY: "invoiceSumsToPay",
  INVOICE_SUMS_TOTAL: "invoiceSumsTotal",

  // Footer fields (readonly)
  INVOICE_OWNER: "invoiceOwner",
  PHONE: "phone",
  EMAIL: "email",
} as const;

const invoiceRowSchema = yup.object({
  lp: yup.string().required("Numer pozycji jest wymagany"),
  nazwa: yup.string().required("Nazwa jest wymagana"),
  jedn: yup.string().required("Jednostka jest wymagana"),
  ilosc: yup.string().required("Ilość jest wymagana"),
  cenaNetto: yup.string().required("Cena netto jest wymagana"),
  stawka: yup.string().required("Stawka VAT jest wymagana"),
  wartoscNetto: yup.string().required("Wartość netto jest wymagana"),
  wartoscBrutton: yup.string().required("Wartość brutto jest wymagana"),
});

const previousAdvancedPaymentSchema = yup.object({
  invoiceNumber: yup.string().required("Numer faktury jest wymagany"),
  amount: yup
    .string()
    .required("Kwota jest wymagana")
    .transform((value) => (value ? value.replace(",", ".") : value))
    .test("is-number", "Wartość musi być liczbą", (value) => !isNaN(Number(value)))
    .test("is-positive", "Kwota musi być większa od 0", (value) => Number(value) >= 0),
  creationDate: yup.date().required("Data utworzenia jest wymagana"),
});

const ratesSettlementSchema = yup.object({
  vatRate: yup.string().required("Stawka VAT jest wymagana"),
  netValue: yup
    .string()
    .required("Wartość netto jest wymagana")
    .transform((value) => (value ? value.replace(",", ".") : value))
    .test("is-number", "Wartość musi być liczbą", (value) => !isNaN(Number(value)))
    .test("is-positive", "Wartość musi być większa od 0", (value) => Number(value) >= 0),
  vatValue: yup
    .string()
    .required("Wartość VAT jest wymagana")
    .transform((value) => (value ? value.replace(",", ".") : value))
    .test("is-number", "Wartość musi być liczbą", (value) => !isNaN(Number(value)))
    .test("is-positive", "Wartość musi być większa od 0", (value) => Number(value) >= 0),
  grossValue: yup
    .string()
    .required("Wartość brutto jest wymagana")
    .transform((value) => (value ? value.replace(",", ".") : value))
    .test("is-number", "Wartość musi być liczbą", (value) => !isNaN(Number(value)))
    .test("is-positive", "Wartość musi być większa od 0", (value) => Number(value) >= 0),
});

const orderSummaryRowSchema = yup.object({
  vatRate: yup.string().required("Stawka VAT jest wymagana"),
  netValue: yup
    .string()
    .required("Wartość netto jest wymagana")
    .transform((value) => (value ? value.replace(",", ".") : value))
    .test("is-number", "Wartość musi być liczbą", (value) => !isNaN(Number(value)))
    .test("is-positive", "Wartość musi być większa od 0", (value) => Number(value) >= 0),
  vatValue: yup
    .string()
    .required("Wartość VAT jest wymagana")
    .transform((value) => (value ? value.replace(",", ".") : value))
    .test("is-number", "Wartość musi być liczbą", (value) => !isNaN(Number(value)))
    .test("is-positive", "Wartość musi być większa od 0", (value) => Number(value) >= 0),
  grossValue: yup
    .string()
    .required("Wartość brutto jest wymagana")
    .transform((value) => (value ? value.replace(",", ".") : value))
    .test("is-number", "Wartość musi być liczbą", (value) => !isNaN(Number(value)))
    .test("is-positive", "Wartość musi być większa od 0", (value) => Number(value) >= 0),
});

const customInvoiceSchema = yup.object({
  // Header fields (readonly)
  [FORM_NAMES.TOP_LEFT_LOGO_PATH]: yup.string(),
  [FORM_NAMES.TOP_RIGHT_TABLE_HEADER]: yup.string(),
  [FORM_NAMES.CREATION_DATE]: yup.date(),
  [FORM_NAMES.SALE_DATE]: yup.date().required("Data sprzedaży jest wymagana"),
  [FORM_NAMES.PAYMENT_METHOD]: yup.string().required("Metoda płatności jest wymagana"),
  [FORM_NAMES.ORDER_NUMBER]: yup.string().required("Numer zamówienia jest wymagany"),

  // Seller fields (readonly)
  [FORM_NAMES.SELLER_NAME]: yup.string(),
  [FORM_NAMES.SELLER_ADDRESS]: yup.string(),
  [FORM_NAMES.SELLER_NIP]: yup.string(),
  [FORM_NAMES.SELLER_BANK_ACCOUNT]: yup.string(),

  // Editable fields
  [FORM_NAMES.ROWS]: yup.array().of(invoiceRowSchema).min(1, "Co najmniej jedna pozycja jest wymagana"),
  [FORM_NAMES.BUYER]: yup.string().nullable(),
  [FORM_NAMES.BUYER_ADDRESS]: yup.string().nullable(),
  [FORM_NAMES.BUYER_NIP]: yup.string().nullable(),
  [FORM_NAMES.RECIPIENT]: yup.string().nullable(),
  [FORM_NAMES.RECIPIENT_ADDRESS]: yup.string().nullable(),
  [FORM_NAMES.RECIPIENT_NIP]: yup.string().nullable(),
  [FORM_NAMES.PREVIOUS_ADVANCED_PAYMENTS]: yup.array().of(previousAdvancedPaymentSchema),
  [FORM_NAMES.RATES_SETTLEMENT]: yup.array().of(ratesSettlementSchema),
  [FORM_NAMES.ORDER_SUMMARY_ROWS]: yup.array().of(orderSummaryRowSchema),

  // Invoice sums (editable)
  [FORM_NAMES.INVOICE_SUMS_PAID]: yup
    .string()
    .required("Zapłacono jest wymagane")
    .transform((value) => (value ? value.replace(",", ".") : value))
    .test("is-number", "Wartość musi być liczbą", (value) => !isNaN(Number(value)))
    .test("is-positive", "Wartość musi być większa od 0", (value) => Number(value) >= 0),
  [FORM_NAMES.INVOICE_SUMS_TO_PAY]: yup
    .string()
    .required("Do zapłaty jest wymagane")
    .transform((value) => (value ? value.replace(",", ".") : value))
    .test("is-number", "Wartość musi być liczbą", (value) => !isNaN(Number(value)))
    .test("is-positive", "Wartość musi być większa od 0", (value) => Number(value) >= 0),
  [FORM_NAMES.INVOICE_SUMS_TOTAL]: yup
    .string()
    .required("Suma jest wymagana")
    .transform((value) => (value ? value.replace(",", ".") : value))
    .test("is-number", "Wartość musi być liczbą", (value) => !isNaN(Number(value)))
    .test("is-positive", "Wartość musi być większa od 0", (value) => Number(value) >= 0),

  // Footer fields (readonly)
  [FORM_NAMES.INVOICE_OWNER]: yup.string(),
  [FORM_NAMES.PHONE]: yup.string(),
  [FORM_NAMES.EMAIL]: yup.string(),
});

type CustomInvoiceFormValues = yup.InferType<typeof customInvoiceSchema>;

export const GenerateCustomInvoiceForm = ({
  configuration,
  onSuccess,
  onCancel,
  paymentId,
  courseId,
}: GenerateCustomInvoiceFormProps) => {
  const { showError, showSuccess } = useToaster();
  const actionRef = useRef<"save" | "saveAndGenerate">("saveAndGenerate");

  // Helper function to check if recipient data exists
  const hasRecipientData = (config: InvoiceConfiguration) => {
    return !!(config.recipient?.recipient || config.recipient?.recipientAddress || config.recipient?.recipientNip);
  };

  const methods = useForm<CustomInvoiceFormValues>({
    resolver: yupResolver(customInvoiceSchema),
    defaultValues: getFormValuesFromConfiguration(configuration),
  });

  // State to control recipient section visibility
  const [showRecipientSection, setShowRecipientSection] = useState(hasRecipientData(configuration));

  // Reset form when configuration changes (e.g., when modal reopens with fresh data)
  useEffect(() => {
    const hasRecipient = hasRecipientData(configuration);
    setShowRecipientSection(hasRecipient);
    const formValues = getFormValuesFromConfiguration(configuration);
    methods.reset(formValues);
  }, [configuration, methods]);

  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: FORM_NAMES.ROWS,
  });

  const {
    fields: advancedPaymentFields,
    append: appendAdvancedPayment,
    remove: removeAdvancedPayment,
  } = useFieldArray({
    control: methods.control,
    name: FORM_NAMES.PREVIOUS_ADVANCED_PAYMENTS,
  });

  const {
    fields: ratesSettlementFields,
    append: appendRatesSettlement,
    remove: removeRatesSettlement,
  } = useFieldArray({
    control: methods.control,
    name: FORM_NAMES.RATES_SETTLEMENT,
  });

  const {
    fields: orderSummaryFields,
    append: appendOrderSummary,
    remove: removeOrderSummary,
  } = useFieldArray({
    control: methods.control,
    name: FORM_NAMES.ORDER_SUMMARY_ROWS,
  });

  const onSubmit = async (data: CustomInvoiceFormValues) => {
    try {
      const requestBody: InvoiceConfiguration = {
        type: configuration.type,
        number: configuration.number,
        year: configuration.year,
        month: configuration.month,
        header: {
          topLeftLogoPath: data.topLeftLogoPath || "",
          topRightTableHeader: data.topRightTableHeader || "",
          creationDate: setDateToEleven(data.creationDate),
          saleDate: setDateToEleven(data.saleDate),
          paymentMethod: data.paymentMethod,
          orderNumber: data.orderNumber,
        },
        seller: {
          name: data.sellerName || "",
          address: data.sellerAddress || "",
          nip: data.sellerNip || "",
          bankAccount: data.sellerBankAccount || "",
        },
        buyer: {
          buyer: data.buyer || null,
          buyerAddress: data.buyerAddress || null,
          buyerNip: data.buyerNip || null,
        },
        recipient:
          data.recipient || data.recipientAddress || data.recipientNip
            ? {
                recipient: data.recipient || null,
                recipientAddress: data.recipientAddress || null,
                recipientNip: data.recipientNip || null,
              }
            : null,
        rows: data.rows || [],
        orderSummaryRows:
          data.orderSummaryRows?.map((row) => ({
            ...row,
            netValue: Number(row.netValue.toString().replace(",", ".")),
            vatValue: Number(row.vatValue.toString().replace(",", ".")),
            grossValue: Number(row.grossValue.toString().replace(",", ".")),
          })) || [],
        invoiceSums: {
          paid: Number(data.invoiceSumsPaid.toString().replace(",", ".")),
          toPay: Number(data.invoiceSumsToPay.toString().replace(",", ".")),
          total: Number(data.invoiceSumsTotal.toString().replace(",", ".")),
        },
        footer: {
          invoiceOwner: data.invoiceOwner || "",
          phone: data.phone || "",
          email: data.email || "",
        },
        previousAdvancedPayments:
          data.previousAdvancedPayments?.map((payment) => ({
            ...payment,
            amount: Number(payment.amount.toString().replace(",", ".")),
            creationDate: setDateToEleven(payment.creationDate),
          })) || [],
        ratesSettlement:
          data.ratesSettlement?.map((rate) => ({
            ...rate,
            netValue: Number(rate.netValue.toString().replace(",", ".")),
            vatValue: Number(rate.vatValue.toString().replace(",", ".")),
            grossValue: Number(rate.grossValue.toString().replace(",", ".")),
          })) || null,
      };

      const saveOnly = actionRef.current === "save";
      console.log({ requestBody: JSON.stringify(requestBody), configuration: JSON.stringify(configuration) });
      await paymentsApi.updateInvoiceConfiguration(paymentId, requestBody, courseId, !saveOnly);
      showSuccess(saveOnly ? "Konfiguracja faktury została zapisana" : "Faktura niestandardowa została wygenerowana");
      onSuccess();
    } catch (error) {
      showError("Nie udało się zapisać konfiguracji faktury");
      console.error("Error saving custom invoice configuration:", error);
    } finally {
      methods.reset();
    }
  };

  const addRow = () => {
    append({
      lp: (fields.length + 1).toString(),
      nazwa: "",
      jedn: "szt",
      ilosc: "1",
      cenaNetto: "0.00",
      stawka: "23",
      wartoscNetto: "0.00",
      wartoscBrutton: "0.00",
    });
  };

  const addAdvancedPayment = () => {
    appendAdvancedPayment({
      invoiceNumber: "",
      amount: "0",
      creationDate: setDateToEleven(new Date()),
    });
  };

  const addRatesSettlement = () => {
    appendRatesSettlement({
      vatRate: "23",
      netValue: "0",
      vatValue: "0",
      grossValue: "0",
    });
  };

  const addOrderSummary = () => {
    appendOrderSummary({
      vatRate: "23",
      netValue: "0",
      vatValue: "0",
      grossValue: "0",
    });
  };

  const toggleRecipientSection = () => {
    if (!showRecipientSection) {
      setShowRecipientSection(true);
    } else {
      // Clear recipient fields when hiding the section
      methods.setValue(FORM_NAMES.RECIPIENT, "");
      methods.setValue(FORM_NAMES.RECIPIENT_ADDRESS, "");
      methods.setValue(FORM_NAMES.RECIPIENT_NIP, "");
      setShowRecipientSection(false);
    }
  };

  const { handleSubmit } = methods;

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex h-full flex-col overflow-hidden"
        key={`invoice-form-${paymentId}-${configuration.number || "new"}`}
      >
        <div className="flex-1 space-y-6 overflow-y-auto pr-2">
          {/* Header Information (readonly) */}
          <div>
            <SectionHeader>Informacje nagłówka</SectionHeader>
            <div className="grid grid-cols-12 gap-2">
              <div className="col-span-6">
                <PaziaInput name={FORM_NAMES.TOP_RIGHT_TABLE_HEADER} label="Nagłówek faktury" disabled />
              </div>
              <div className="col-span-6">
                <PaziaDatepicker name={FORM_NAMES.CREATION_DATE} label="Data utworzenia" disabled />
              </div>
              <div className="col-span-6">
                <PaziaDatepicker name={FORM_NAMES.SALE_DATE} label="Data sprzedaży" required />
              </div>
              <div className="col-span-6">
                <PaziaInput name={FORM_NAMES.PAYMENT_METHOD} label="Metoda płatności" required />
              </div>
              <div className="col-span-6">
                <PaziaInput name={FORM_NAMES.ORDER_NUMBER} label="Numer zamówienia" required />
              </div>
              <div className="col-span-6">
                <PaziaInput name={FORM_NAMES.TOP_LEFT_LOGO_PATH} label="Ścieżka do logo" disabled />
              </div>
            </div>
          </div>

          {/* Seller Information (readonly) */}
          <div>
            <SectionHeader>Dane sprzedawcy</SectionHeader>
            <div className="grid grid-cols-12 gap-2">
              <div className="col-span-12">
                <PaziaInput name={FORM_NAMES.SELLER_NAME} label="Nazwa sprzedawcy" disabled />
              </div>
              <div className="col-span-12">
                <PaziaTextarea name={FORM_NAMES.SELLER_ADDRESS} label="Adres sprzedawcy" rows={2} disabled />
              </div>
              <div className="col-span-6">
                <PaziaInput name={FORM_NAMES.SELLER_NIP} label="NIP sprzedawcy" disabled />
              </div>
              <div className="col-span-6">
                <PaziaInput name={FORM_NAMES.SELLER_BANK_ACCOUNT} label="Konto bankowe" disabled />
              </div>
            </div>
          </div>

          {/* Buyer Information */}
          <div>
            <SectionHeader>Dane nabywcy</SectionHeader>
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-6">
                <PaziaInput name={FORM_NAMES.BUYER} label="Nabywca" />
              </div>
              <div className="col-span-6">
                <PaziaInput name={FORM_NAMES.BUYER_NIP} label="NIP nabywcy" />
              </div>
              <div className="col-span-12">
                <PaziaTextarea name={FORM_NAMES.BUYER_ADDRESS} label="Adres nabywcy" rows={2} />
              </div>
            </div>
          </div>

          {/* Recipient Information */}
          <div>
            <div className="mb-3 flex items-center justify-between">
              <SectionHeader>Dane odbiorcy (opcjonalne)</SectionHeader>
              <PaziaButton
                type="tertiary"
                btnType="button"
                onClick={toggleRecipientSection}
                title={showRecipientSection ? "Usuń dane odbiorcy" : "Dodaj dane odbiorcy"}
              />
            </div>

            {showRecipientSection && (
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-6">
                  <PaziaInput name={FORM_NAMES.RECIPIENT} label="Odbiorca" />
                </div>
                <div className="col-span-6">
                  <PaziaInput name={FORM_NAMES.RECIPIENT_NIP} label="NIP odbiorcy" />
                </div>
                <div className="col-span-12">
                  <PaziaTextarea name={FORM_NAMES.RECIPIENT_ADDRESS} label="Adres odbiorcy" rows={2} />
                </div>
              </div>
            )}
          </div>

          {/* Invoice Rows */}
          <div>
            <div className="mb-3 flex items-center justify-between">
              <SectionHeader>Pozycje faktury</SectionHeader>
              <PaziaButton type="tertiary" btnType="button" onClick={addRow} title="Dodaj pozycję" />
            </div>

            <div className="space-y-4">
              {fields.map((field, index) => (
                <div key={field.id} className="rounded-lg border bg-gray-50 p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <h4 className="font-medium">Pozycja {index + 1}</h4>
                    {fields.length > 1 && <DeleteButton onClick={() => remove(index)} />}
                  </div>

                  <div className="grid grid-cols-12 gap-3">
                    <div className="col-span-1">
                      <PaziaInput name={`${FORM_NAMES.ROWS}.${index}.lp`} label="Lp." required hideErrors />
                    </div>
                    <div className="col-span-11">
                      <PaziaInput name={`${FORM_NAMES.ROWS}.${index}.nazwa`} label="Nazwa" required hideErrors />
                    </div>
                    <div className="col-span-2">
                      <PaziaInput name={`${FORM_NAMES.ROWS}.${index}.jedn`} label="Jednostka" required hideErrors />
                    </div>
                    <div className="col-span-2">
                      <PaziaInput name={`${FORM_NAMES.ROWS}.${index}.ilosc`} label="Ilość" required hideErrors />
                    </div>
                    <div className="col-span-2">
                      <PaziaInput
                        name={`${FORM_NAMES.ROWS}.${index}.cenaNetto`}
                        label="Cena netto"
                        required
                        hideErrors
                      />
                    </div>
                    <div className="col-span-2">
                      <PaziaInput
                        name={`${FORM_NAMES.ROWS}.${index}.stawka`}
                        label="Stawka VAT (%)"
                        required
                        hideErrors
                      />
                    </div>
                    <div className="col-span-2">
                      <PaziaInput
                        name={`${FORM_NAMES.ROWS}.${index}.wartoscNetto`}
                        label="Wartość netto"
                        required
                        hideErrors
                      />
                    </div>
                    <div className="col-span-2">
                      <PaziaInput
                        name={`${FORM_NAMES.ROWS}.${index}.wartoscBrutton`}
                        label="Wartość brutto"
                        required
                        hideErrors
                      />
                    </div>
                    <div className="col-span-4"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Order Summary Rows */}
          <div>
            <div className="mb-3 flex items-center justify-between">
              <SectionHeader>Podsumowanie zamówienia</SectionHeader>
              <PaziaButton type="tertiary" btnType="button" onClick={addOrderSummary} title="Dodaj podsumowanie" />
            </div>

            <div className="space-y-4">
              {orderSummaryFields.length === 0 ? (
                <div className="rounded-lg border-2 border-dashed border-gray-300 p-8 text-center text-gray-500">
                  Brak podsumowania zamówienia. Kliknij "Dodaj podsumowanie" aby dodać nową pozycję.
                </div>
              ) : (
                orderSummaryFields.map((field, index) => (
                  <div key={field.id} className="rounded-lg border bg-yellow-50 p-4">
                    <div className="mb-2 flex items-center justify-between">
                      <h4 className="font-medium">Podsumowanie {index + 1}</h4>
                      <DeleteButton onClick={() => removeOrderSummary(index)} />
                    </div>

                    <div className="grid grid-cols-12 gap-3">
                      <div className="col-span-3">
                        <PaziaInput
                          name={`${FORM_NAMES.ORDER_SUMMARY_ROWS}.${index}.vatRate`}
                          label="Stawka VAT"
                          required
                          hideErrors
                        />
                      </div>
                      <div className="col-span-3">
                        <PaziaInput
                          name={`${FORM_NAMES.ORDER_SUMMARY_ROWS}.${index}.netValue`}
                          label="Wartość netto"
                          required
                          hideErrors
                        />
                      </div>
                      <div className="col-span-3">
                        <PaziaInput
                          name={`${FORM_NAMES.ORDER_SUMMARY_ROWS}.${index}.vatValue`}
                          label="Wartość VAT"
                          required
                          hideErrors
                        />
                      </div>
                      <div className="col-span-3">
                        <PaziaInput
                          name={`${FORM_NAMES.ORDER_SUMMARY_ROWS}.${index}.grossValue`}
                          label="Wartość brutto"
                          required
                          hideErrors
                        />
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Invoice Sums */}
          <div>
            <SectionHeader>Podsumowanie faktury</SectionHeader>
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-4">
                <PaziaInput name={FORM_NAMES.INVOICE_SUMS_PAID} label="Zapłacono (PLN)" required />
              </div>
              <div className="col-span-4">
                <PaziaInput name={FORM_NAMES.INVOICE_SUMS_TO_PAY} label="Do zapłaty (PLN)" required />
              </div>
              <div className="col-span-4">
                <PaziaInput name={FORM_NAMES.INVOICE_SUMS_TOTAL} label="Suma (PLN)" required />
              </div>
            </div>
          </div>

          {/* Previous Advanced Payments */}
          <div>
            <div className="mb-3 flex items-center justify-between">
              <SectionHeader>Poprzednie zaliczki</SectionHeader>
              <PaziaButton type="tertiary" btnType="button" onClick={addAdvancedPayment} title="Dodaj zaliczkę" />
            </div>

            <div className="space-y-4">
              {advancedPaymentFields.length === 0 ? (
                <div className="rounded-lg border-2 border-dashed border-gray-300 p-8 text-center text-gray-500">
                  Brak zaliczek. Kliknij "Dodaj zaliczkę" aby dodać nową pozycję.
                </div>
              ) : (
                advancedPaymentFields.map((field, index) => (
                  <div key={field.id} className="rounded-lg border bg-blue-50 p-4">
                    <div className="mb-2 flex items-center justify-between">
                      <h4 className="font-medium">Zaliczka {index + 1}</h4>
                      <DeleteButton onClick={() => removeAdvancedPayment(index)} />
                    </div>

                    <div className="grid grid-cols-12 gap-3">
                      <div className="col-span-6">
                        <PaziaInput
                          name={`${FORM_NAMES.PREVIOUS_ADVANCED_PAYMENTS}.${index}.invoiceNumber`}
                          label="Numer faktury zaliczkowej"
                          required
                          hideErrors
                        />
                      </div>
                      <div className="col-span-3">
                        <PaziaInput
                          name={`${FORM_NAMES.PREVIOUS_ADVANCED_PAYMENTS}.${index}.amount`}
                          label="Kwota"
                          required
                          hideErrors
                        />
                      </div>
                      <div className="col-span-3">
                        <PaziaDatepicker
                          name={`${FORM_NAMES.PREVIOUS_ADVANCED_PAYMENTS}.${index}.creationDate`}
                          label="Data utworzenia"
                          required
                        />
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Rates Settlement */}
          <div>
            <div className="mb-3 flex items-center justify-between">
              <SectionHeader>Rozliczenie stawek VAT</SectionHeader>
              <PaziaButton type="tertiary" btnType="button" onClick={addRatesSettlement} title="Dodaj stawkę" />
            </div>

            <div className="space-y-4">
              {ratesSettlementFields.length === 0 ? (
                <div className="rounded-lg border-2 border-dashed border-gray-300 p-8 text-center text-gray-500">
                  Brak rozliczenia stawek VAT. Kliknij "Dodaj stawkę" aby dodać nową pozycję.
                </div>
              ) : (
                ratesSettlementFields.map((field, index) => (
                  <div key={field.id} className="rounded-lg border bg-green-50 p-4">
                    <div className="mb-2 flex items-center justify-between">
                      <h4 className="font-medium">Stawka VAT {index + 1}</h4>
                      <DeleteButton onClick={() => removeRatesSettlement(index)} />
                    </div>

                    <div className="grid grid-cols-12 gap-3">
                      <div className="col-span-3">
                        <PaziaInput
                          name={`${FORM_NAMES.RATES_SETTLEMENT}.${index}.vatRate`}
                          label="Stawka VAT"
                          required
                          hideErrors
                        />
                      </div>
                      <div className="col-span-3">
                        <PaziaInput
                          name={`${FORM_NAMES.RATES_SETTLEMENT}.${index}.netValue`}
                          label="Wartość netto"
                          required
                          hideErrors
                        />
                      </div>
                      <div className="col-span-3">
                        <PaziaInput
                          name={`${FORM_NAMES.RATES_SETTLEMENT}.${index}.vatValue`}
                          label="Wartość VAT"
                          required
                          hideErrors
                        />
                      </div>
                      <div className="col-span-3">
                        <PaziaInput
                          name={`${FORM_NAMES.RATES_SETTLEMENT}.${index}.grossValue`}
                          label="Wartość brutto"
                          required
                          hideErrors
                        />
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Footer Information (readonly) */}
          <div>
            <SectionHeader>Informacje stopki</SectionHeader>
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-4">
                <PaziaInput name={FORM_NAMES.INVOICE_OWNER} label="Właściciel faktury" disabled />
              </div>
              <div className="col-span-4">
                <PaziaInput name={FORM_NAMES.PHONE} label="Telefon" disabled />
              </div>
              <div className="col-span-4">
                <PaziaInput name={FORM_NAMES.EMAIL} label="Email" disabled />
              </div>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="mt-4 flex flex-shrink-0 justify-end gap-2 border-t pt-4">
          <PaziaButton type="tertiary" btnType="button" onClick={onCancel} title="Anuluj" />
          <PaziaFormButton
            allowCleanSubmit
            type="secondary"
            title="Zapisz"
            onClick={() => {
              actionRef.current = "save";
            }}
          />
          <PaziaFormButton
            allowCleanSubmit
            disabled={true} // TODO
            title="Zapisz i wygeneruj fakturę"
            onClick={() => {
              actionRef.current = "saveAndGenerate";
            }}
          />
        </div>
      </form>
    </FormProvider>
  );
};

const SectionHeader = ({ children }: { children: string }) => (
  <h3 className="mb-3 text-lg font-medium text-primary-700">{children}</h3>
);

// Helper function to get form values from configuration
const getFormValuesFromConfiguration = (config: InvoiceConfiguration): CustomInvoiceFormValues => ({
  [FORM_NAMES.TOP_LEFT_LOGO_PATH]: config.header?.topLeftLogoPath || "",
  [FORM_NAMES.TOP_RIGHT_TABLE_HEADER]: config.header?.topRightTableHeader || "",
  [FORM_NAMES.CREATION_DATE]: config.header?.creationDate
    ? setDateToEleven(new Date(config.header.creationDate))
    : setDateToEleven(new Date()),
  [FORM_NAMES.SALE_DATE]: config.header?.saleDate
    ? setDateToEleven(new Date(config.header.saleDate))
    : setDateToEleven(new Date()),
  [FORM_NAMES.PAYMENT_METHOD]: config.header?.paymentMethod || "",
  [FORM_NAMES.ORDER_NUMBER]: config.header?.orderNumber || "",

  // Seller fields (readonly)
  [FORM_NAMES.SELLER_NAME]: config.seller?.name || "",
  [FORM_NAMES.SELLER_ADDRESS]: config.seller?.address || "",
  [FORM_NAMES.SELLER_NIP]: config.seller?.nip || "",
  [FORM_NAMES.SELLER_BANK_ACCOUNT]: config.seller?.bankAccount || "",

  // Editable fields
  [FORM_NAMES.ROWS]: config.rows || [],
  [FORM_NAMES.BUYER]: config.buyer?.buyer || "",
  [FORM_NAMES.BUYER_ADDRESS]: config.buyer?.buyerAddress || "",
  [FORM_NAMES.BUYER_NIP]: config.buyer?.buyerNip || "",
  [FORM_NAMES.RECIPIENT]: config.recipient?.recipient || "",
  [FORM_NAMES.RECIPIENT_ADDRESS]: config.recipient?.recipientAddress || "",
  [FORM_NAMES.RECIPIENT_NIP]: config.recipient?.recipientNip || "",
  [FORM_NAMES.PREVIOUS_ADVANCED_PAYMENTS]:
    config.previousAdvancedPayments?.map((payment) => ({
      ...payment,
      amount: payment.amount.toString(),
      creationDate: setDateToEleven(new Date(payment.creationDate)),
    })) || [],
  [FORM_NAMES.RATES_SETTLEMENT]:
    config.ratesSettlement?.map((rate) => ({
      ...rate,
      netValue: rate.netValue.toString(),
      vatValue: rate.vatValue.toString(),
      grossValue: rate.grossValue.toString(),
    })) || [],
  [FORM_NAMES.ORDER_SUMMARY_ROWS]:
    config.orderSummaryRows?.map((row) => ({
      ...row,
      netValue: row.netValue.toString(),
      vatValue: row.vatValue.toString(),
      grossValue: row.grossValue.toString(),
    })) || [],

  // Invoice sums (editable)
  [FORM_NAMES.INVOICE_SUMS_PAID]: config.invoiceSums?.paid?.toString() || "0",
  [FORM_NAMES.INVOICE_SUMS_TO_PAY]: config.invoiceSums?.toPay?.toString() || "0",
  [FORM_NAMES.INVOICE_SUMS_TOTAL]: config.invoiceSums?.total?.toString() || "0",

  // Footer fields (readonly)
  [FORM_NAMES.INVOICE_OWNER]: config.footer?.invoiceOwner || "",
  [FORM_NAMES.PHONE]: config.footer?.phone || "",
  [FORM_NAMES.EMAIL]: config.footer?.email || "",
});
